import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import Photography from './pages/Photography';
import PhotoshootDetail from './pages/PhotoshootDetail';
import Yoga from './pages/Yoga';
import Art from './pages/Art';
import Admin from './pages/Admin';
import {
  loadSiteData,
  saveSiteData,
  loadPhotos,
  loadPhotoshoots,
  loadServices,
  loadArtPieces
} from './services/dataService';
import './App.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

function App() {
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);

        // Load main site data
        const mainData = await loadSiteData();

        // Load collection data
        const [photos, photoshoots, services, artPieces] = await Promise.all([
          loadPhotos(),
          loadPhotoshoots(),
          loadServices(),
          loadArtPieces()
        ]);

        // Combine all data
        const completeData = {
          ...mainData,
          photography: {
            ...mainData.photography,
            photos,
            photoshoots
          },
          yoga: {
            ...mainData.yoga,
            services
          },
          art: {
            ...mainData.art,
            pieces: artPieces
          }
        };

        setSiteData(completeData);
      } catch (error) {
        console.error('Failed to load site data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const updateSiteData = async (section, data) => {
    try {
      await saveSiteData(section, data);
      setSiteData(prev => ({
        ...prev,
        [section]: data
      }));
    } catch (error) {
      console.error('Failed to save data:', error);
      throw error;
    }
  };

  const refreshCollectionData = async (collectionType) => {
    try {
      let data;
      switch (collectionType) {
        case 'photos':
          data = await loadPhotos();
          setSiteData(prev => ({
            ...prev,
            photography: {
              ...prev.photography,
              photos: data
            }
          }));
          break;
        case 'photoshoots':
          data = await loadPhotoshoots();
          setSiteData(prev => ({
            ...prev,
            photography: {
              ...prev.photography,
              photoshoots: data
            }
          }));
          break;
        case 'services':
          data = await loadServices();
          setSiteData(prev => ({
            ...prev,
            yoga: {
              ...prev.yoga,
              services: data
            }
          }));
          break;
        case 'artPieces':
          data = await loadArtPieces();
          setSiteData(prev => ({
            ...prev,
            art: {
              ...prev.art,
              pieces: data
            }
          }));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Failed to refresh ${collectionType}:`, error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sage-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load Site</h1>
          <p className="text-gray-600 mb-6">There was an error loading the website data. Please check your internet connection and try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-sage-600 text-white px-6 py-3 rounded-lg hover:bg-sage-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/admin" element={
              <Admin
                siteData={siteData}
                setSiteData={setSiteData}
                updateSiteData={updateSiteData}
                refreshCollectionData={refreshCollectionData}
              />
            } />
            <Route path="/*" element={
              <Layout siteData={siteData}>
                <Routes>
                  <Route path="/" element={<Home siteData={siteData} />} />
                  <Route path="/photography" element={<Photography siteData={siteData} />} />
                  <Route path="/photography/:photoshootId" element={<PhotoshootDetail siteData={siteData} />} />
                  <Route path="/yoga" element={<Yoga siteData={siteData} />} />
                  <Route path="/art" element={<Art siteData={siteData} />} />
                </Routes>
              </Layout>
            } />
          </Routes>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;